import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useAdminCustomers } from "@/hooks/useAdmin";
import { formatDate } from "@/lib/utils";

export function AdminCustomersPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch]           = React.useState("");
  const [page, setPage]               = React.useState(1);

  // Debounce search so each keystroke doesn't hit the API.
  React.useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = useAdminCustomers({ search, page, pageSize: 50 });

  return (
    <div className="space-y-4">
      <Header value={searchInput} onChange={setSearchInput} />

      {isLoading ? (
        <ListSkeleton />
      ) : !data || data.items.length === 0 ? (
        <EmptyState searching={Boolean(search)} />
      ) : (
        <>
          {/* Desktop */}
          <Card className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Active plan</TableHead>
                  <TableHead>Domains</TableHead>
                  <TableHead>HostShop ID</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map(c => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/admin/customers/${c.id}`)}
                  >
                    <TableCell>
                      <p className="font-medium">{c.user_email ?? "—"}</p>
                      {c.user_full_name && <p className="text-xs text-muted-foreground">{c.user_full_name}</p>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.company_name ?? "—"}</TableCell>
                    <TableCell>
                      {c.active_plan_name ? <Badge variant="success">{c.active_plan_name}</Badge>
                                          : <Badge variant="outline">No plan</Badge>}
                    </TableCell>
                    <TableCell className="text-sm">{c.domains_count}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{c.hostshop_customer_id ?? "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(c.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile */}
          <div className="grid gap-3 md:hidden">
            {data.items.map(c => (
              <Card
                key={c.id}
                className="cursor-pointer"
                onClick={() => navigate(`/admin/customers/${c.id}`)}
              >
                <CardContent className="space-y-2 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{c.user_email ?? "—"}</p>
                      {c.company_name && <p className="text-xs text-muted-foreground">{c.company_name}</p>}
                    </div>
                    {c.active_plan_name ? <Badge variant="success">{c.active_plan_name}</Badge>
                                        : <Badge variant="outline">No plan</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {c.domains_count} domains · joined {formatDate(c.created_at)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPrev={() => setPage(p => Math.max(1, p - 1))}
            onNext={() => setPage(p => p + 1)}
          />
        </>
      )}
    </div>
  );
}

function Header({ value, onChange }: { value: string; onChange: (n: string) => void }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">Customers</h2>
        <p className="text-sm text-muted-foreground">Every account on the platform.</p>
      </div>
      <div className="relative w-full sm:max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Search email, company, domain..."
          className="pl-9"
        />
      </div>
    </div>
  );
}

function Pagination({ page, pageSize, total, onPrev, onNext }: {
  page: number; pageSize: number; total: number; onPrev: () => void; onNext: () => void;
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);
  const hasNext = page * pageSize < total;
  return (
    <div className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
      <span className="text-muted-foreground">Showing {from}–{to} of {total}</span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onPrev} disabled={page === 1}>Previous</Button>
        <Button variant="outline" size="sm" onClick={onNext} disabled={!hasNext}>Next</Button>
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <Card>
      <CardContent className="space-y-2 py-5">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
      </CardContent>
    </Card>
  );
}

function EmptyState({ searching }: { searching: boolean }) {
  return (
    <Card>
      <CardContent className="py-12 text-center text-sm text-muted-foreground">
        {searching ? "No customers match that search." : "No customers yet."}
      </CardContent>
    </Card>
  );
}
